import { translateLabel } from "@/i18n/messages";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowRightIcon,
  MapPinIcon,
  PlusIcon,
  SparkleIcon,
  SpinnerGapIcon,
  ShareNetworkIcon,
  UsersThreeIcon,
  XIcon,
} from "@phosphor-icons/react";
import { Navbar } from "@/components/Navbar";
import { PlanThisRideButton } from "@/components/PlanThisRideButton";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { destinationCoverImage } from "@/lib/destinationCover";
import { parkPath } from "@/lib/routes";
import { buildComparePath } from "@/lib/compareSpots";
import { formatDistanceKm } from "@/lib/units";
import { useUnitPreference } from "@/context/UnitPreferenceContext";
import { trackEvent } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";

const STYLE_OPTIONS = [
  ["flow", "Flow"],
  ["downhill", "Downhill"],
  ["enduro", "Enduro"],
  ["freeride", "Freeride"],
  ["jump", "Jump"],
  ["family", "Family"],
];

const emptyOrigin = () => "";

const parseOrigins = (searchParams) => {
  const values = [1, 2, 3, 4]
    .map((index) => searchParams.get(`o${index}`)?.trim() || "")
    .filter(Boolean);
  return values.length >= 2 ? values : [emptyOrigin(), emptyOrigin()];
};

const parseMaxDrive = (searchParams) => {
  const value = Number(searchParams.get("max"));
  return Number.isFinite(value) && value >= 15 && value <= 480 ? value : 165;
};

const parseAccess = (searchParams) => {
  const value = searchParams.get("access") || "";
  return ["lift", "pedal"].includes(value) ? value : "";
};

const parseStyles = (searchParams) => {
  const allowed = new Set(STYLE_OPTIONS.map(([value]) => value));
  return (searchParams.get("styles") || "")
    .split(",")
    .map((value) => value.trim())
    .filter((value) => allowed.has(value));
};

const parseDate = (searchParams) => {
  const value = searchParams.get("date") || "";
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : "";
};

const formatDrive = (minutes) => {
  if (minutes == null) return "Unavailable";
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h ? `${h}h${m ? ` ${m}m` : ""}` : `${m} min`;
};

export default function GroupDiscovery() {
  const { unitSystem } = useUnitPreference();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [origins, setOrigins] = useState(() => parseOrigins(searchParams));
  const [maxDriveMinutes, setMaxDriveMinutes] = useState(() => parseMaxDrive(searchParams));
  const [access, setAccess] = useState(() => parseAccess(searchParams));
  const [styles, setStyles] = useState(() => parseStyles(searchParams));
  const [date, setDate] = useState(() => parseDate(searchParams));
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiMessage, setAiMessage] = useState("");
  const [sharing, setSharing] = useState(false);
  const [originClarification, setOriginClarification] = useState(null);

  const updateOrigin = (index, value) => {
    setOrigins((current) => current.map((item, itemIndex) => itemIndex === index ? value : item));
  };

  const removeOrigin = (index) => {
    setOrigins((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const toggleStyle = (value) => {
    setStyles((current) => current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value]);
  };

  useEffect(() => {
    const next = new URLSearchParams();
    origins.map((item) => item.trim()).filter(Boolean).slice(0, 4)
      .forEach((item, index) => next.set(`o${index + 1}`, item));
    next.set("max", String(maxDriveMinutes));
    if (access) next.set("access", access);
    if (styles.length) next.set("styles", styles.join(","));
    if (date) next.set("date", date);
    setSearchParams(next, { replace: true });
  }, [origins, maxDriveMinutes, access, styles, date, setSearchParams]);

  const applyAiCriteria = async () => {
    const message = aiPrompt.trim();
    if (!message) return;

    setAiBusy(true);
    setAiMessage("");
    setError("");
    try {
      const response = await api.post("/discovery/interpret", {
        message,
        current_criteria: {
          ...(access ? { access: [access] } : {}),
          ...(styles.length ? { styles } : {}),
        },
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Berlin",
      });
      const interpretation = response.data;
      const intent = interpretation?.intent || {};

      if (interpretation?.clarifications?.length) {
        setAiMessage(interpretation.clarifications.join(" "));
        return;
      }
      if (interpretation?.unsupported_criteria?.length) {
        setAiMessage(`Not supported in Group Discovery yet: ${interpretation.unsupported_criteria.join(", ")}.`);
        return;
      }

      if (Array.isArray(intent.origins) && intent.origins.length >= 2) {
        setOrigins(intent.origins.slice(0, 4));
      }
      if (Number.isFinite(intent.max_drive_minutes)) {
        setMaxDriveMinutes(intent.max_drive_minutes);
      }
      if (Array.isArray(intent.access) && intent.access.length) {
        const supportedAccess = intent.access.filter((value) => ["lift", "pedal"].includes(value));
        if (supportedAccess.length === 1) setAccess(supportedAccess[0]);
        else if (intent.access.length > 1 || supportedAccess.length === 0) {
          setAiMessage("The request contains an access combination the current Group Discovery form cannot represent yet.");
        }
      }
      if (Array.isArray(intent.styles) && intent.styles.length) {
        const allowed = new Set(STYLE_OPTIONS.map(([value]) => value));
        setStyles(intent.styles.filter((value) => allowed.has(value)));
      }
      if (intent.date && /^\d{4}-\d{2}-\d{2}$/.test(intent.date)) {
        setDate(intent.date);
      }

      if (!interpretation?.clarifications?.length && !interpretation?.unsupported_criteria?.length) {
        setAiMessage(interpretation?.explanation || "Group criteria updated. Review them, then run the search.");
      }
      trackEvent("group_discovery_ai_handoff", {
        origin_count: intent.origins?.length || 0,
        has_max_drive: intent.max_drive_minutes != null,
        access_count: intent.access?.length || 0,
        style_count: intent.styles?.length || 0,
      });
    } catch (requestError) {
      setAiMessage(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setAiBusy(false);
    }
  };

  const search = async () => {
    const cleanOrigins = origins.map((item) => item.trim()).filter(Boolean);
    if (cleanOrigins.length < 2) {
      setError(translateLabel("Add at least two starting locations."));
      return;
    }

    setLoading(true);
    setError("");
    setOriginClarification(null);
    setResult(null);
    try {
      const response = await api.post("/group-discovery", {
        origins: cleanOrigins,
        max_drive_minutes: Number(maxDriveMinutes),
        access: access ? [access] : [],
        styles,
        date: date || undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Berlin",
      });
      setResult(response.data);
      trackEvent("group_discovery_search", {
        origin_count: cleanOrigins.length,
        max_drive_minutes: Number(maxDriveMinutes),
        access: access || "any",
        styles: styles.join(",") || "any",
        date: date || null,
        result_count: response.data?.recommendations?.length || 0,
      });
      window.requestAnimationFrame(() => {
        document.querySelector("[data-testid='group-discovery-results']")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (requestError) {
      const detail = formatApiErrorDetail(requestError.response?.data?.detail);
      setError(detail);
      const match = String(detail || "").match(/Several locations match '([^']+)'.*?:\s*(.+)$/i);
      if (match) {
        const query = match[1];
        const options = match[2].split(";").map((item) => item.trim()).filter(Boolean);
        if (options.length > 1) setOriginClarification({ query, options });
      }
    } finally {
      setLoading(false);
    }
  };

  const createVotingRoom = async () => {
    if (!result?.recommendations?.length) return;
    const returnTo = `${location.pathname}${location.search || ""}`;
    if (!user) {
      navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`);
      return;
    }

    const hostOrigin = result.origin || result.origins?.[0];
    if (!hostOrigin) {
      setError(translateLabel("Run Group Discovery again so the group starting points can be verified."));
      return;
    }

    setSharing(true);
    setError("");
    try {
      const response = await api.post("/ride-plans", {
        date: result.date || date || new Date().toISOString().slice(0, 10),
        origin: {
          label: hostOrigin.label,
          lat: hostOrigin.lat,
          lng: hostOrigin.lng,
        },
        max_drive_minutes: Number(maxDriveMinutes),
        wants: styles,
        access: access || undefined,
        ride_mode: "GROUP_DISCOVERY",
        candidates: result.recommendations.slice(0, 20).map((recommendation) => ({
          destination_id: recommendation.park.id,
          name: recommendation.park.name,
          city: recommendation.park.city || null,
          country: recommendation.park.country || null,
          lat: recommendation.park.lat ?? null,
          lng: recommendation.park.lng ?? null,
          drive_minutes: recommendation.travel?.drive_minutes ?? null,
          drive_km: recommendation.travel?.drive_km ?? null,
          weather_condition: recommendation.weather?.condition ?? null,
          weather_temp_min_c: recommendation.weather?.temp_min_c ?? null,
          weather_temp_max_c: recommendation.weather?.temp_max_c ?? null,
          precipitation_probability_pct: recommendation.weather?.precipitation_probability_pct ?? null,
          day_ticket_price: recommendation.day_ticket_price ?? recommendation.park.day_ticket_price ?? null,
          image: destinationCoverImage(recommendation.park),
        })),
      });

      trackEvent("group_discovery_voting_room_created", {
        candidate_count: Math.min(result.recommendations.length, 20),
        origin_count: result.origins?.length || 0,
      });
      navigate(`/ride-plan/${response.data.room.plan.share_token}`);
    } catch (requestError) {
      setError(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setSharing(false);
    }
  };

  const recommendations = result?.recommendations || [];
  const groupRoutesByDestination = new Map(
    (result?.group_routes || []).map((routeSet) => [routeSet.destination_id, routeSet.routes || []]),
  );

  return (
    <div className="min-h-[calc(100dvh-var(--app-footer-height))] bg-[#09090b] text-white">
      <Navbar scrollsWithPage brandVariant="mark" />
      <main className="mx-auto max-w-6xl px-4 pb-16 pt-24 sm:px-6 md:pt-28">
        <header className="max-w-3xl">
          <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.18em] text-[#CCFF00]">
            <UsersThreeIcon size={17} weight="bold" /> {translateLabel("Group Discovery")}
          </div>
          <h1 className="mt-2 font-display text-4xl font-black uppercase leading-[0.94] sm:text-5xl">
            {translateLabel("Find the park that works for everyone.")}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-400 sm:text-base">
            {translateLabel("Add where each rider starts. The drive limit is a hard constraint for every starting point — not an average.")}
          </p>
        </header>

        <section className="mt-7 rounded-3xl border border-white/[0.08] bg-[#121214] p-4 sm:p-6">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.16em] text-zinc-500">{translateLabel("Group criteria")}</div>
              <p className="mt-1 text-xs text-zinc-500">{translateLabel("Set the group manually, or use the assistant to fill the same fields.")}</p>
            </div>
            <button
              type="button"
              onClick={() => setAiOpen((value) => !value)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-[11px] font-bold text-zinc-300 hover:border-[#CCFF00]/25 hover:text-[#CCFF00]"
              aria-expanded={aiOpen}
              data-testid="toggle-group-ai"
            >
              <SparkleIcon size={13} weight="fill" /> {aiOpen ? translateLabel("Hide assistant") : translateLabel("Fill with AI")}
            </button>
          </div>

          {aiOpen && (
            <div className="mb-6 rounded-2xl border border-[#CCFF00]/15 bg-[#CCFF00]/[0.035] p-3 sm:p-4">
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  value={aiPrompt}
                  onChange={(event) => setAiPrompt(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      applyAiCriteria();
                    }
                  }}
                  placeholder={translateLabel("From Würzburg, Frankfurt and Ingolstadt, lift + flow, max 2h45")}
                  className="min-w-0 flex-1 rounded-xl border border-white/10 bg-black/25 px-3 py-3 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-[#CCFF00]/40"
                  data-testid="group-ai-prompt"
                />
                <button
                  type="button"
                  onClick={applyAiCriteria}
                  disabled={aiBusy || !aiPrompt.trim()}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#CCFF00]/30 bg-[#CCFF00]/10 px-4 py-3 text-sm font-extrabold text-[#CCFF00] hover:bg-[#CCFF00]/15 disabled:opacity-40"
                  data-testid="apply-group-ai"
                >
                  {aiBusy ? <SpinnerGapIcon size={17} className="animate-spin" /> : <SparkleIcon size={16} weight="fill" />}
                  {aiBusy ? translateLabel("Filling…") : translateLabel("Apply to form")}
                </button>
              </div>
              {aiMessage && (
                <p className="mt-2 text-xs leading-5 text-zinc-300" data-testid="group-ai-message">{aiMessage}</p>
              )}
            </div>
          )}
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(18rem,0.85fr)]">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.16em] text-zinc-500">{translateLabel("Starting locations")}</div>
              <div className="mt-3 space-y-2">
                {origins.map((origin, index) => (
                  <div key={index} className="flex gap-2">
                    <div className="relative min-w-0 flex-1">
                      <MapPinIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#CCFF00]" />
                      <input
                        value={origin}
                        onChange={(event) => updateOrigin(index, event.target.value)}
                        placeholder={index === 0 ? "e.g. Würzburg" : index === 1 ? "e.g. Frankfurt" : translateLabel("Another rider's city or postcode")}
                        className="w-full rounded-xl border border-white/10 bg-black/25 py-3 pl-10 pr-3 text-sm outline-none placeholder:text-zinc-600 focus:border-[#CCFF00]/40"
                        data-testid={`group-origin-${index}`}
                      />
                    </div>
                    {origins.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeOrigin(index)}
                        aria-label={translateLabel("Remove starting location")}
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 text-zinc-500 hover:text-white"
                      >
                        <XIcon size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {origins.length < 4 && (
                <button
                  type="button"
                  onClick={() => setOrigins((current) => [...current, emptyOrigin()])}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-[#CCFF00]"
                >
                  <PlusIcon size={14} weight="bold" /> {translateLabel("Add rider location")}
                </button>
              )}
            </div>

            <div className="space-y-5">
              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-zinc-500">{translateLabel("Nobody drives more than")}</span>
                <div className="mt-2 flex items-center gap-3">
                  <input
                    type="range"
                    min="15"
                    max="480"
                    step="15"
                    value={maxDriveMinutes}
                    onChange={(event) => setMaxDriveMinutes(Number(event.target.value))}
                    className="min-w-0 flex-1 accent-[#CCFF00]"
                  />
                  <span className="w-16 text-right text-sm font-extrabold text-white">{formatDrive(maxDriveMinutes)}</span>
                </div>
              </label>

              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-zinc-500">{translateLabel("Access")}</span>
                <select
                  value={access}
                  onChange={(event) => setAccess(event.target.value)}
                  className="mt-2 w-full rounded-xl border border-white/10 bg-black/25 px-3 py-3 text-sm text-white outline-none"
                >
                  <option value="">{translateLabel("Any access")}</option>
                  <option value="lift">{translateLabel("Lift")}</option>
                  <option value="pedal">{translateLabel("Pedal")}</option>
                </select>
              </label>

              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-zinc-500">{translateLabel("Ride date")}</span>
                <input
                  type="date"
                  value={date}
                  onChange={(event) => setDate(event.target.value)}
                  className="mt-2 w-full rounded-xl border border-white/10 bg-black/25 px-3 py-3 text-sm text-white outline-none"
                  data-testid="group-discovery-date"
                />
              </label>
            </div>
          </div>

          <div className="mt-6 border-t border-white/[0.06] pt-5">
            <div className="text-[10px] font-black uppercase tracking-[0.16em] text-zinc-500">{translateLabel("We want")}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {STYLE_OPTIONS.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => toggleStyle(value)}
                  aria-pressed={styles.includes(value)}
                  className={`rounded-full border px-3 py-2 text-xs font-bold transition-colors ${
                    styles.includes(value)
                      ? "border-[#CCFF00]/50 bg-[#CCFF00]/12 text-[#CCFF00]"
                      : "border-white/10 text-zinc-400 hover:text-white"
                  }`}
                >
                  {translateLabel(label)}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <p className="mt-4 rounded-xl border border-red-400/20 bg-red-400/10 px-3 py-2.5 text-xs font-semibold text-red-200">
              {error}
            </p>
          )}

          {originClarification && (
            <div className="mt-3 rounded-xl border border-amber-300/20 bg-amber-300/[0.06] px-3 py-3" data-testid="group-origin-clarification">
              <div className="text-[10px] font-black uppercase tracking-[0.14em] text-amber-200">
                {translateLabel("Which {query}?", { query: originClarification.query })}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {originClarification.options.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      setOrigins((current) => current.map((item) => (
                        item.trim().toLowerCase() === originClarification.query.trim().toLowerCase()
                          ? option
                          : item
                      )));
                      setOriginClarification(null);
                      setError("");
                    }}
                    className="rounded-full border border-amber-200/20 bg-black/20 px-3 py-1.5 text-[11px] font-bold text-amber-100 hover:border-[#CCFF00]/35 hover:text-[#CCFF00]"
                  >
                    {option}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-[10px] leading-4 text-zinc-500">
                {translateLabel("Choose the intended place, then run the group search again.")}
              </p>
            </div>
          )}

          <button
            type="button"
            onClick={search}
            disabled={loading}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#CCFF00] px-5 py-3.5 text-sm font-black text-black hover:bg-[#B8E600] disabled:opacity-50 sm:w-auto"
            data-testid="find-group-parks"
          >
            {loading ? <SpinnerGapIcon size={18} className="animate-spin" /> : <UsersThreeIcon size={18} weight="bold" />}
            {loading ? translateLabel("Checking routes…") : translateLabel("Find parks for the group")}
          </button>
        </section>

        {result && (
          <section className="mt-8 scroll-mt-24" data-testid="group-discovery-results">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="label-tiny text-[#CCFF00]">{translateLabel("Group matches")}</div>
                <h2 className="mt-1 font-display text-3xl font-black uppercase">
                  {result.match_kind === "EXACT"
                    ? translateLabel(recommendations.length === 1 ? "{count} park fits the hard constraints" : "{count} parks fit the hard constraints", { count: recommendations.length })
                    : recommendations.length
                      ? translateLabel(recommendations.length === 1 ? "{count} candidate needs verification" : "{count} candidates need verification", { count: recommendations.length })
                      : translateLabel("No verified match")}
                </h2>
                <div className="mt-1 text-xs text-zinc-500">
                  {result.origins?.map((item) => item.label).join(" + ")}
                </div>
              </div>
              {recommendations.length > 0 && result.routing_available && (
                <button
                  type="button"
                  onClick={createVotingRoom}
                  disabled={sharing}
                  className="inline-flex self-start items-center gap-2 rounded-xl border border-[#CCFF00]/30 bg-[#CCFF00]/10 px-4 py-2.5 text-xs font-extrabold text-[#CCFF00] hover:bg-[#CCFF00]/15 disabled:opacity-45 sm:self-auto"
                  data-testid="group-create-voting-room"
                >
                  {sharing
                    ? <SpinnerGapIcon size={15} className="animate-spin" />
                    : <ShareNetworkIcon size={15} weight="bold" />}
                  {sharing ? translateLabel("Creating…") : translateLabel("Share & vote")}
                </button>
              )}
            </div>

            {!result.routing_available && (
              <div className="mt-4 rounded-xl border border-amber-300/20 bg-amber-300/[0.08] px-4 py-3 text-xs leading-5 text-amber-100">
                {translateLabel("Drive-time routing is unavailable. Group drive limits cannot be verified, so these results must not be treated as confirmed matches.")}
              </div>
            )}

            {result.unverified_reasons?.map((reason) => (
              <p key={reason} className="mt-2 text-xs leading-5 text-amber-200">⚠ {reason}</p>
            ))}

            {recommendations.length > 0 ? (
              <div className="mt-5 grid gap-4 md:grid-cols-2">
                {recommendations.map((recommendation) => {
                  const park = recommendation.park;
                  const groupRoutes = groupRoutesByDestination.get(park.id) || [];
                  return (
                    <article key={park.id} className="overflow-hidden rounded-2xl border border-white/[0.08] bg-[#121214]">
                      <div className="grid grid-cols-[8rem_minmax(0,1fr)] sm:grid-cols-[10rem_minmax(0,1fr)]">
                        <img src={destinationCoverImage(park)} alt="" className="h-full min-h-40 w-full object-cover" />
                        <div className="min-w-0 p-4">
                          <div className="text-[10px] font-black uppercase tracking-[0.14em] text-[#CCFF00]">
                            {translateLabel("Max group drive")} · {formatDrive(recommendation.travel?.drive_minutes)}
                          </div>
                          <h3 className="mt-1 font-display text-xl font-black uppercase leading-tight">{park.name}</h3>
                          <p className="mt-1 text-xs text-zinc-500">{[park.city, park.country].filter(Boolean).join(", ")}</p>
                          <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-400">
                            <span>{park.trail_count ?? 0} trails</span>
                          </div>

                          {groupRoutes.length > 0 && (
                            <div className="mt-3 space-y-1.5" data-testid={`group-routes-${park.id}`}>
                              {groupRoutes.map((route) => (
                                <div
                                  key={`${park.id}-${translateLabel(route.origin.label)}-${route.origin.lat}-${route.origin.lng}`}
                                  className={`flex items-center justify-between gap-3 rounded-lg border px-2.5 py-2 text-[11px] ${
                                    route.longest
                                      ? "border-[#CCFF00]/30 bg-[#CCFF00]/[0.07]"
                                      : "border-white/[0.06] bg-black/15"
                                  }`}
                                >
                                  <span className="min-w-0 truncate font-semibold text-zinc-300">{translateLabel(route.origin.label)}</span>
                                  <span className={`shrink-0 font-extrabold ${route.longest ? "text-[#CCFF00]" : "text-white"}`}>
                                    {formatDrive(route.drive_minutes)}
                                    {route.drive_km != null ? ` · ${formatDistanceKm(route.drive_km, unitSystem)}` : ""}
                                    {route.longest ? ` · ${translateLabel("Longest")}` : ""}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="mt-4 flex flex-wrap items-center gap-2">
                            <Link to={parkPath(park)} className="inline-flex items-center gap-1 text-xs font-extrabold text-[#CCFF00]">
                              {translateLabel("View park")} <ArrowRightIcon size={13} weight="bold" />
                            </Link>
                            <Link
                              to={buildComparePath([park], {
                                searchCenter: result?.origin ? {
                                  lat: result.origin.lat,
                                  lng: result.origin.lng,
                                  name: result.origin.label,
                                } : undefined,
                                date: date || undefined,
                                returnTo: `${location.pathname}${location.search}`,
                              })}
                              className="inline-flex rounded-lg border border-white/10 px-2.5 py-1.5 text-[11px] font-bold text-zinc-300 hover:text-white"
                            >
                              {translateLabel("Compare")}
                            </Link>
                            <PlanThisRideButton
                              destination={park}
                              initialDate={date}
                              initialOrigin={result?.origin || null}
                              source="group_discovery"
                              compact
                            />
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="mt-5 rounded-2xl border border-white/[0.08] bg-[#121214] p-6 text-sm leading-6 text-zinc-400">
                {result.summary}
                <div className="mt-2 text-xs text-zinc-600">{translateLabel("No constraint was relaxed automatically.")}</div>
              </div>
            )}

            {!result.evaluation_complete && (
              <p className="mt-4 text-xs leading-5 text-zinc-600">
                {translateLabel("Candidate evaluation was bounded; this is not an exhaustive statement about every destination.")}
              </p>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
